import { css, keyframes, useTheme } from '@emotion/react';
import { formatDuration } from '@sonematic/shared';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { darkTheme } from '@/theme/dark-theme';
import { fontSizes, mq, spacing } from '@/theme/tokens';

export interface RehearsalResultsProps {
  title?: string;
  totalWords: number;
  sections: Array<{
    position: number;
    title: string | null;
    estimatedDurationMs: number;
    measuredDurationMs: number;
  }>;
  totalEstimatedMs: number;
  totalMeasuredMs: number;
  onSave: () => void;
  onDiscard: () => void;
  onBack: () => void;
  onRehearsalAgain: () => void;
  saving: boolean;
}

function formatDelta(deltaMs: number): string {
  const abs = Math.abs(deltaMs);
  const sign = deltaMs >= 0 ? '+' : '-';
  return `${sign}${formatDuration(abs)}`;
}

function deltaColor(deltaMs: number): string {
  if (deltaMs < 0) return darkTheme.success;
  if (deltaMs > 0) return darkTheme.error;
  return darkTheme.text;
}

function paceLabel(deltaMs: number): string {
  const absSec = Math.abs(deltaMs) / 1000;
  if (absSec < 5) return 'Right on pace';
  const formatted = formatDuration(Math.abs(deltaMs));
  if (deltaMs < 0) return `${formatted} ahead of pace`;
  return `${formatted} behind pace`;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const OFF_PACE_THRESHOLD_MS = 15000;

export function RehearsalResults({
  title,
  totalWords,
  sections,
  totalEstimatedMs,
  totalMeasuredMs,
  onSave,
  onDiscard,
  onBack,
  onRehearsalAgain,
  saving,
}: RehearsalResultsProps) {
  const theme = useTheme();
  const totalDelta = totalMeasuredMs - totalEstimatedMs;
  const avgWpm = totalMeasuredMs > 0 ? Math.round(totalWords / (totalMeasuredMs / 60000)) : 0;

  const completedSections = sections.filter((s) => s.measuredDurationMs > 0);
  const skippedSections = sections.filter((s) => s.measuredDurationMs === 0);
  const isPartial = skippedSections.length > 0;

  return (
    <div
      css={css`
        min-height: 100vh;
        background: ${darkTheme.background};
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: ${spacing.xxl} ${spacing.lg};
        color: ${darkTheme.text};
      `}
    >
      {/* Nav bar */}
      <div
        css={css`
          width: 100%;
          display: flex;
          align-items: center;
          margin-bottom: ${spacing.xl};
        `}
      >
        <button
          type="button"
          onClick={onBack}
          css={css`
            background: none;
            border: none;
            color: ${theme.colors.textSecondary};
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: ${fontSizes.sm};
            padding: ${spacing.xs};

            &:hover {
              color: ${theme.colors.text};
            }
          `}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back to Editor
        </button>
      </div>

      {/* Header */}
      <div
        css={css`
          text-align: center;
          margin-bottom: ${spacing.lg};
          animation: ${fadeIn} 0.3s ease-out;
        `}
      >
        <h1
          css={css`
            margin: 0;
            font-size: ${fontSizes.xxl};
            font-weight: 600;
          `}
        >
          {title || 'Untitled Speech'}
        </h1>
        <p
          css={css`
            margin: ${spacing.sm} 0 0;
            font-size: ${fontSizes.lg};
            color: ${deltaColor(totalDelta)};
            font-weight: 500;
          `}
        >
          {paceLabel(totalDelta)}
        </p>
        {isPartial && (
          <p
            css={css`
              margin: ${spacing.xs} 0 0;
              font-size: ${fontSizes.sm};
              color: ${darkTheme.warning};
            `}
          >
            Partial rehearsal — {completedSections.length} of {sections.length} sections completed
          </p>
        )}
      </div>

      {/* Summary stats */}
      <div
        css={css`
          display: flex;
          gap: ${spacing.md};
          margin-bottom: ${spacing.xl};
          animation: ${fadeIn} 0.3s ease-out 0.1s both;
        `}
      >
        <StatCard label="Total Time" value={formatDuration(totalMeasuredMs)} />
        <StatCard
          label="vs. Estimate"
          value={formatDelta(totalDelta)}
          color={deltaColor(totalDelta)}
        />
        <StatCard label="Avg WPM" value={String(avgWpm)} />
      </div>

      {/* Table in a card container */}
      <Card
        padding={spacing.md}
        css={css`
          width: 100%;
          max-width: 720px;
          margin-bottom: ${spacing.xl};
          animation: ${fadeIn} 0.4s ease-out 0.2s both;

          ${mq.lg} {
            max-width: 800px;
          }
        `}
      >
        <table
          css={css`
            border-collapse: collapse;
            width: 100%;
          `}
        >
          <thead>
            <tr>
              <Th align="left">Section</Th>
              <Th align="right">Estimated</Th>
              <Th align="right">Measured</Th>
              <Th align="right">vs. Est.</Th>
            </tr>
          </thead>
          <tbody>
            {completedSections.map((section) => {
              const delta = section.measuredDurationMs - section.estimatedDurationMs;
              const offPace = Math.abs(delta) > OFF_PACE_THRESHOLD_MS;
              return (
                <tr key={section.position}>
                  <td
                    css={css`
                      padding: 10px ${spacing.md};
                      font-weight: 500;
                      border-left: 3px solid ${offPace ? deltaColor(delta) : 'transparent'};
                    `}
                  >
                    {section.title ?? `Section ${section.position + 1}`}
                  </td>
                  <Td>{formatDuration(section.estimatedDurationMs)}</Td>
                  <Td>{formatDuration(section.measuredDurationMs)}</Td>
                  <Td color={deltaColor(delta)}>{formatDelta(delta)}</Td>
                </tr>
              );
            })}

            {/* Skipped sections — collapsed summary */}
            {skippedSections.length > 0 && (
              <tr>
                <td
                  colSpan={4}
                  css={css`
                    padding: 10px ${spacing.md};
                    color: ${darkTheme.textMuted};
                    font-size: ${fontSizes.sm};
                    font-style: italic;
                  `}
                >
                  {skippedSections.length} section{skippedSections.length !== 1 ? 's' : ''} not
                  reached
                </td>
              </tr>
            )}

            {/* Totals row */}
            <tr>
              <td
                css={css`
                  padding: 14px ${spacing.md} 6px;
                  font-weight: 600;
                  border-top: 1px solid ${darkTheme.border};
                `}
              >
                Total
              </td>
              <TdTotal>{formatDuration(totalEstimatedMs)}</TdTotal>
              <TdTotal>{formatDuration(totalMeasuredMs)}</TdTotal>
              <TdTotal color={deltaColor(totalDelta)}>{formatDelta(totalDelta)}</TdTotal>
            </tr>
          </tbody>
        </table>
      </Card>

      {/* Actions */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          animation: ${fadeIn} 0.4s ease-out 0.3s both;
        `}
      >
        <div
          css={css`
            display: flex;
            gap: ${spacing.md};
          `}
        >
          <Button onClick={onSave} disabled={saving} size="lg">
            {saving ? 'Saving...' : 'Save Rehearsal'}
          </Button>
          <Button variant="secondary" onClick={onRehearsalAgain} size="lg">
            Rehearse Again
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDiscard}
          css={css`
            margin-top: ${spacing.md};
          `}
        >
          Discard
        </Button>
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function Th({ align, children }: { align: 'left' | 'right'; children: React.ReactNode }) {
  return (
    <th
      css={css`
        text-align: ${align};
        padding: ${spacing.sm} ${spacing.md};
        color: ${darkTheme.textSecondary};
        font-size: ${fontSizes.xs};
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        border-bottom: 1px solid ${darkTheme.border};
      `}
    >
      {children}
    </th>
  );
}

function Td({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <td
      css={css`
        text-align: right;
        padding: 10px ${spacing.md};
        font-variant-numeric: tabular-nums;
        font-size: ${fontSizes.sm};
        color: ${color ?? darkTheme.textSecondary};
      `}
    >
      {children}
    </td>
  );
}

function TdTotal({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <td
      css={css`
        text-align: right;
        padding: 14px ${spacing.md} 6px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        border-top: 1px solid ${darkTheme.border};
        ${color ? `color: ${color};` : ''}
      `}
    >
      {children}
    </td>
  );
}
